import { IonIcon, IonLabel, IonPopover } from '@ionic/react';
import { caretDown } from 'ionicons/icons';
import React, {
  FunctionComponent,
  ReactElement,
  ReactNode,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { ButtonProps } from './base-button';
import './button.css';
import { LoadingButton } from './loading-button';

interface PopoverChildrenProps {
  close: () => void;
}

export type ActionButtonChildrenType =
  | ReactElement<PopoverChildrenProps>
  | FunctionComponent<PopoverChildrenProps>;

export interface ActionButtonProps {
  children: ActionButtonChildrenType;
  label?: string | ReactElement;
  icon?: ReactNode;
  buttonProps?: ButtonProps;
  className?: string;
  loading?: boolean;
  preventPopoverOnClick?: () => boolean;
}

export interface ActionButtonRef {
  present: () => void;
  dismiss: () => void;
}

const CUSTOM_EVENT_DETAIL = 1000;

const ActionButton = forwardRef<ActionButtonRef | undefined, ActionButtonProps>(
  (props: ActionButtonProps, ref): ReactElement => {
    const { children, icon, label, buttonProps, className, loading } = props;

    const dismiss = useCallback(() => {
      setIsOpen(false);
    }, []);

    const childProps: PopoverChildrenProps = {
      close: dismiss,
    };

    const buttonRef = useRef<HTMLIonButtonElement | null>(null);
    const eventRef = useRef<MouseEvent | null>(null);

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const dispatchManualClick = useCallback(() => {
      if (buttonRef.current) {
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true,
          detail: CUSTOM_EVENT_DETAIL,
        });

        buttonRef.current.dispatchEvent(clickEvent);
      }
    }, []);

    useImperativeHandle(
      ref,
      () => {
        return {
          present: dispatchManualClick,
          dismiss,
        };
      },
      [dispatchManualClick, dismiss]
    );

    const onClick = (e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();

      let preventPopover = false;

      if (typeof props.preventPopoverOnClick === 'function') {
        preventPopover = props.preventPopoverOnClick();
      }

      if (e.nativeEvent.detail === CUSTOM_EVENT_DETAIL) {
        if (!preventPopover) {
          eventRef.current = e.nativeEvent;
          setIsOpen(true);
        }
      } else if (buttonRef.current) {
        dispatchManualClick();

        if (props.buttonProps?.onClick) {
          props.buttonProps?.onClick(e);
        }
      }
    };

    return (
      <>
        <LoadingButton
          ref={buttonRef}
          buttonProps={{
            ...buttonProps,
            onClick,
          }}
          className={className}
          loading={loading ?? false}
        >
          {!icon && (
            <IonLabel>
              {label && label}
              <IonIcon className="action-button-icon" icon={caretDown} />
            </IonLabel>
          )}
          {icon && icon}
        </LoadingButton>
        <IonPopover
          isOpen={isOpen}
          event={eventRef.current}
          onDidDismiss={dismiss}
        >
          {typeof children === 'function'
            ? children({ ...childProps })
            : React.cloneElement(children, childProps)}
        </IonPopover>
      </>
    );
  }
);

ActionButton.displayName = 'ActionButton';

export { ActionButton };
